import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatUnits } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { useUserData } from '../hooks/useUserData';
import { usePlanConfig } from '../hooks/usePlanConfig';
import { useRecentActivity } from '../hooks/useRecentActivity';
import { useTeamStats } from '../hooks/useTeamStats';
import { useIncomeBreakdown } from '../hooks/useIncomeBreakdown';
import { useContracts } from '../hooks/useContracts';
import { useNotify } from '../context/NotificationContext';
import { fmtUSD, fmtGRX, truncateAddress, tsToDate } from '../utils/format';
import { describeActivity, timeAgo, ACT_CATEGORIES, categoryOfActType } from '../utils/activity';
import { parseError } from '../utils/errors';
import GlassCard from '../components/common/GlassCard';
import NeonButton from '../components/common/NeonButton';
import CopyButton from '../components/common/CopyButton';
import DonutChart from '../components/common/DonutChart';
import Speedometer from '../components/common/Speedometer';
import ProgressBar from '../components/common/ProgressBar';
import ConnectPrompt from '../components/common/ConnectPrompt';
import AtomSpinner from '../components/loader/AtomSpinner';
import HexLoader from '../components/loader/HexLoader';
import Icon from '../components/common/Icon';
import VideoTiles from '../components/common/VideoTiles';
import CutoffTimer from '../components/common/CutoffTimer';
import { useCutoffInfo } from '../hooks/useCutoffInfo';
import { sumInvPending } from '../utils/roi';
import { CEILING_LABEL, rankTag, rankLabel } from '../utils/constants';

const PAGE = 10;

const Dashboard = () => {
  const nav = useNavigate();
  const { account } = useWallet();
  const c = useContracts();
  const notify = useNotify();
  const { loading, userId, summary, investments, grxBalance, usdtBalance } = useUserData();
  const { config } = usePlanConfig();
  const activity = useRecentActivity();
  const team = useTeamStats();
  const income = useIncomeBreakdown();
  const { currentCutoffIndex, info: cutoffInfo } = useCutoffInfo();

  // Today's (this cutoff's) level income — sum of activity events with
  // actType 6 (level_income) that landed after the current cutoff started.
  const todayLevelIncome = useMemo(() => {
    if (!activity?.events || !cutoffInfo) return 0n;
    const period = BigInt(cutoffInfo.cutoffPeriod || 0);
    const nextAt = BigInt(cutoffInfo.nextCutoffAt || 0);
    const cutoffStart = nextAt > period ? nextAt - period : 0n;
    let sum = 0n;
    for (const ev of activity.events) {
      if (Number(ev.actType) !== 6) continue;
      if (BigInt(ev.ts) < cutoffStart) continue;
      sum += BigInt(ev.netAmount || 0);
    }
    return sum;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.events, cutoffInfo]);

  const [busy, setBusy] = useState(false);
  const [txLabel, setTxLabel] = useState('');

  // Activity view state
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [actFilter, setActFilter] = useState('all');

  // ----------------- derived -----------------
  const grxPriceStr = useMemo(() => {
    if (!config?.grxPriceUSDT) return '—';
    try { return '$' + parseFloat(formatUnits(config.grxPriceUSDT.toString(), 18)).toFixed(6); } catch { return '—'; }
  }, [config]);

  const backendRank = Number(summary?.rank || 0);
  const ceilBps = Number(summary?.ceilingBps || 20000);
  const ceilLabel = CEILING_LABEL[ceilBps] || '2X';
  const pending = summary?.pendingRewardsUSDT || 0n;
  const uncredited = useMemo(
    () => sumInvPending(investments, currentCutoffIndex),
    [investments, currentCutoffIndex]
  );
  const totalAvailable = BigInt(pending) + BigInt(uncredited);
  // GRX equivalent of the full available balance (credited + uncredited)
  const totalAvailableGRX = useMemo(() => {
    const priceRaw = config?.grxPriceUSDT ? BigInt(config.grxPriceUSDT) : 0n;
    if (priceRaw === 0n || totalAvailable === 0n) return 0n;
    return (totalAvailable * (10n ** 18n)) / priceRaw;
  }, [totalAvailable, config]);

  // Ceiling speedometer — sum of active investment caps vs earned
  const ceiling = useMemo(() => {
    if (!investments || !investments.length) return { earned: 0n, cap: 0n, pct: 0 };
    let earned = 0n, cap = 0n;
    for (const inv of investments) {
      const amt = BigInt(inv.amountUSDT);
      const c2 = (amt * BigInt(ceilBps)) / 10000n;
      cap += c2;
      earned += BigInt(inv.bucketEarnedUSDT);
    }
    const pct = cap > 0n ? Number((earned * 10000n) / cap) / 100 : 0;
    return { earned, cap, pct };
  }, [investments, ceilBps]);

  // Joined date — from earliest investment or first activity
  const joinedDate = useMemo(() => {
    if (investments && investments.length) {
      let earliest = Number(investments[0].timestamp);
      for (const inv of investments) {
        const t = Number(inv.timestamp);
        if (t && t < earliest) earliest = t;
      }
      return tsToDate(earliest);
    }
    if (activity.events && activity.events.length) {
      // events are newest-first; find oldest
      let earliest = activity.events[0].ts;
      for (const ev of activity.events) if (ev.ts < earliest) earliest = ev.ts;
      return tsToDate(earliest);
    }
    return '—';
  }, [investments, activity.events]);

  // Income breakdown slices
  const isContributor = summary && Number(summary.contributorCount) > 0;
  // Show any slice with actual income — sponsor income is earned by uplines
  // even when they aren't contributors themselves, so gating on isContributor
  // would hide real earnings from the donut.
  const incomeSlices = useMemo(() => {
    const s = [
      { label: 'Farming Profit', value: income.roi,   color: '#00ff9d' },
      { label: 'Level Income',   value: income.level, color: '#00e5ff' },
      { label: 'Rank Income',    value: income.rank,  color: '#7fff00' },
    ];
    // Sponsor income (from downlines' 10X claims) — shown to any upline earning it
    if ((income.contributorSponsor || 0n) > 0n) {
      s.push({ label: 'Contributor Sponsor', value: income.contributorSponsor, color: '#ffb300' });
    }
    // Own 10X releases — shown only to actual contributors
    if ((income.contributorPayout || 0n) > 0n) {
      s.push({ label: 'Contributor Payout', value: income.contributorPayout, color: '#ff8b3d' });
    }
    return s;
  }, [income]);

  const refLink = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}#/register?ref=${userId || ''}`
    : '';

  // Custom claim amount (USDT-denominated). Empty → claim all.
  const [claimAmountStr, setClaimAmountStr] = useState('');

  // ----------------- actions -----------------
  const claim = async () => {
    try {
      if (!c.write.rewards) throw new Error('Rewards contract not configured.');
      setBusy(true); setTxLabel('Claiming Rewards');

      // If user typed an amount, parse it as USDT (18dp). Otherwise claim all.
      let target;
      if (claimAmountStr && claimAmountStr.trim()) {
        try {
          const { parseUSD } = await import('../utils/format');
          target = parseUSD(claimAmountStr);
        } catch {
          throw new Error('Invalid claim amount.');
        }
        if (target > totalAvailable) throw new Error('Amount exceeds available balance.');
      } else {
        target = totalAvailable > 0n ? totalAvailable : (pending || 0n);
      }
      if (target === 0n) throw new Error('Nothing to claim yet.');

      const tx = await c.write.rewards.claim(target);
      await tx.wait();
      notify.success('Claimed', 'Rewards sent to your wallet.');
      setClaimAmountStr('');
    } catch (e) { notify.error('Claim Failed', parseError(e)); }
    finally { setBusy(false); setTxLabel(''); }
  };

  const sync = async () => {
    try {
      setBusy(true); setTxLabel('Syncing Farming Profit');
      const tx = await c.write.logic.accrueUser(userId);
      await tx.wait();
      notify.success('Synced', 'Farming Profit updated on-chain.');
    } catch (e) { notify.error('Sync Failed', parseError(e)); }
    finally { setBusy(false); setTxLabel(''); }
  };

  // ----------------- render -----------------
  if (!account) return <ConnectPrompt />;
  if (loading) return (
    <div className="empty-state">
      <AtomSpinner size="lg" />
      <div className="empty-title mt-3">Loading Data</div>
    </div>
  );
  if (!userId || userId === 0n) {
    return (
      <div className="page-header">
        <div style={{ width: '100%' }}>
          <div className="page-title">Welcome</div>
          <div className="alert a-info mt-3">
            You are not registered yet. Please <Link to="/register">register</Link> to begin.
          </div>
        </div>
      </div>
    );
  }

  // Filter first, then paginate.
  const filteredEvents = actFilter === 'all'
    ? activity.events
    : activity.events.filter((ev) => categoryOfActType(ev.actType) === actFilter);
  const shownEvents = expanded
    ? filteredEvents.slice(page * PAGE, page * PAGE + PAGE)
    : filteredEvents.slice(0, 5);
  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / PAGE));

  return (
    <div className="animate-fade-up">
      {/* ─────────── WELCOME HERO ─────────── */}
      <div className="dashboard-hero">
        <div className="hero-inner">
          <div className="hero-greeting">
            <div className="hero-avatar hero-avatar-logo">
              <img src="/Logo.png" alt="GRX" />
            </div>
            <div className="hero-greet-text">
              <div className="hg-label">Welcome Back</div>
              <div className="hg-userid">#{String(userId)}</div>
              <div className="hg-address">
                <span className="mono">{truncateAddress(account)}</span>
                <CopyButton value={account} label="Copy" />
              </div>
            </div>
          </div>

          <div className="hero-meta-grid">
            <div className="hero-meta">
              <div className="hm-icon"><Icon name="link" size={16} /></div>
              <div className="hm-label">Upline ID</div>
              <div className="hm-value">#{String(summary?.referrerId || 0)}</div>
            </div>
            <div className="hero-meta">
              <div className="hm-icon"><Icon name="calendar" size={16} /></div>
              <div className="hm-label">Joined</div>
              <div className="hm-value" style={{ fontSize: 13 }}>{joinedDate}</div>
            </div>
            <div className="hero-meta">
              <div className="hm-icon cyan"><Icon name="dollar" size={16} /></div>
              <div className="hm-label">GRX Price</div>
              <div className="hm-value cyan">{grxPriceStr}</div>
            </div>
            <div className="hero-meta">
              <div className="hm-icon"><Icon name="crown" size={16} /></div>
              <div className="hm-label">Rank</div>
              <div className="hm-value">
                {rankTag(backendRank)} · <span className="text-neon">{rankLabel(backendRank)}</span>
              </div>
            </div>
            <div className="hero-meta">
              <div className="hm-icon"><Icon name="activity" size={16} /></div>
              <div className="hm-label">Status</div>
              <div className="hm-value">
                {summary?.blacklisted ? <span className="badge b-danger">Blacklisted</span>
                  : summary?.active ? <span className="badge">Active · {ceilLabel}</span>
                  : <span className="badge b-dim">Inactive</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─────────── WALLET BALANCES (compact) ─────────── */}
      <div className="wallet-strip">
        <div className="wallet-strip-pill">
          <div className="wsp-icon">G</div>
          <div className="wsp-label">GRX Wallet</div>
          <div className="wsp-value">{fmtGRX(grxBalance)}</div>
        </div>
        <div className="wallet-strip-pill">
          <div className="wsp-icon" style={{ color: 'var(--grx-cyan)', background: 'rgba(0, 229, 255, 0.1)' }}>$</div>
          <div className="wsp-label">USDT Wallet</div>
          <div className="wsp-value">{fmtUSD(usdtBalance)}</div>
        </div>
      </div>

      {/* ─────────── REFERRAL LINK (active users only) ─────────── */}
      {summary?.active ? (
        <div className="ref-block">
          <div className="rb-icon"><Icon name="team" size={20} /></div>
          <div className="rb-body">
            <div className="rb-label">Your Referral Link · ID #{String(userId)}</div>
            <div className="rb-url">{refLink}</div>
          </div>
          <CopyButton value={refLink} label="Copy" />
        </div>
      ) : (
        <div className="alert a-warn" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>🔒</span>
          <span>
            <strong>Referral link locked.</strong>{' '}
            <Link to="/invest" style={{ color: 'var(--grx-neon)', textDecoration: 'underline' }}>Make your first investment</Link>
            {' '}to activate your account and unlock your referral link.
          </span>
        </div>
      )}

      {/* ─────────── CONTRIBUTOR CALLOUT (only if user is a contributor) ─────────── */}
      {isContributor && (
        <button className="contrib-callout" onClick={() => nav('/contributor')} type="button" style={{ width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}>
          <div className="cc-icon"><Icon name="contributor" size={26} /></div>
          <div className="cc-body">
            <div className="cc-title">Contributor Payouts Available</div>
            <div className="cc-sub">
              {String(summary.contributorCount)} contributor record{Number(summary.contributorCount) === 1 ? '' : 's'} · 10X payouts released every cycle. Tap to view schedule and claim.
            </div>
          </div>
          <span className="cc-badge">10X</span>
          <Icon name="arrowRight" size={18} className="dim" />
        </button>
      )}

      {/* ─────────── AVAILABLE BALANCE BIG (with mini cutoff top-right) ─────────── */}
      <div className="balance-big">
        <div className="bb-corner-timer">
          <CutoffTimer mini />
        </div>
        <div className="bb-label">Available Balance</div>
        <div className="bb-value">{fmtUSD(totalAvailable)}</div>
        <div className="bb-grx-line">
          <span className="bb-grx-tag">GRX</span>
          <span className="bb-grx-val">{fmtGRX(totalAvailableGRX)}</span>
          <span className="bb-grx-sub">≈ {grxPriceStr} / GRX</span>
        </div>
        <div className="bb-claim-row">
          <div className="bb-claim-input-wrap">
            <input
              type="number"
              placeholder={`Claim amount (max ${fmtUSD(totalAvailable)})`}
              value={claimAmountStr}
              onChange={(e) => setClaimAmountStr(e.target.value)}
              className="bb-claim-input"
            />
            <button
              type="button"
              className="bb-claim-max"
              onClick={() => {
                if (totalAvailable === 0n) return;
                const asStr = (Number(totalAvailable) / 1e18).toFixed(6).replace(/\.?0+$/, '');
                setClaimAmountStr(asStr);
              }}
            >MAX</button>
          </div>
        </div>
        <div className="bb-actions">
          <NeonButton size="lg" onClick={claim} loading={busy} disabled={totalAvailable === 0n}>
            {claimAmountStr && claimAmountStr.trim() ? `Claim ${claimAmountStr} USDT` : 'Claim All Rewards'}
          </NeonButton>
          <NeonButton variant="ghost" size="lg" onClick={sync} loading={busy}>
            Sync Farming
          </NeonButton>
        </div>
      </div>

      {/* ─────────── INVEST + EARNED + AVAILABLE FARMING PROFIT ─────────── */}
      <div className="grid grid-3 mb-4">
        <div className="stat-card-action">
          <div className="sca-icon"><Icon name="chartBar" size={22} /></div>
          <div className="sca-label">Total Invested</div>
          <div className="sca-value">{fmtUSD(summary?.totalInvestmentUSDT)}</div>
          <div className="mono mt-2" style={{ fontSize: 12, color: 'var(--grx-text-dim)' }}>
            Active: {fmtUSD(summary?.activeInvestmentUSDT)}
          </div>
        </div>
        <div className="stat-card-action">
          <div className="sca-icon cyan"><Icon name="trendingUp" size={22} /></div>
          <div className="sca-label">Total Earned</div>
          <div className="sca-value cyan">{fmtUSD(summary?.totalEarnedUSDT)}</div>
          <div className="mono mt-2" style={{ fontSize: 12, color: 'var(--grx-text-dim)' }}>
            Lifetime rewards (all sources)
          </div>
        </div>
        <div className="stat-card-action">
          <div className="sca-icon"><Icon name="sparkle" size={22} /></div>
          <div className="sca-label">Available Farming Profit</div>
          <div className="sca-value neon">{fmtUSD(totalAvailable)}</div>
          <div className="mono mt-2" style={{ fontSize: 11, color: 'var(--grx-text-mute)' }}>
            {fmtUSD(pending)} credited · {fmtUSD(uncredited)} uncredited
          </div>
          <div className="sca-actions">
            <NeonButton variant="ghost" size="sm" onClick={sync} loading={busy}>Sync</NeonButton>
            <NeonButton size="sm" onClick={claim} loading={busy} disabled={totalAvailable === 0n}>Claim</NeonButton>
          </div>
        </div>
      </div>

      {/* ─────────── TEAM STATS ─────────── */}
      <GlassCard title="Team Network">
        <div className="team-grid">
          <div className="team-tile">
            <div className="tt-icon"><Icon name="users" size={20} /></div>
            <div className="tt-label">Total Team</div>
            <div className="tt-value neon">{String(team.totalTeam)}</div>
            <div className="tt-sub">across 30 levels</div>
          </div>
          <div className="team-tile">
            <div className="tt-icon"><Icon name="userPlus" size={20} /></div>
            <div className="tt-label">Direct Referrals</div>
            <div className="tt-value">{String(team.directCount)}</div>
            <div className="tt-sub">L1 referrals</div>
          </div>
          <div className="team-tile">
            <div className="tt-icon cyan"><Icon name="briefcase" size={20} /></div>
            <div className="tt-label">Total Business</div>
            <div className="tt-value cyan">{fmtUSD(team.totalBiz)}</div>
            <div className="tt-sub">tree-wide volume</div>
          </div>
          <div className="team-tile">
            <div className="tt-icon"><Icon name="dollar" size={20} /></div>
            <div className="tt-label">Direct Business</div>
            <div className="tt-value">{fmtUSD(team.directBiz)}</div>
            <div className="tt-sub">L1 leg total</div>
          </div>
          <div className="team-tile">
            <div className="tt-icon cyan"><Icon name="trendingUp" size={20} /></div>
            <div className="tt-label">Today's Level Income</div>
            <div className="tt-value cyan">{fmtUSD(todayLevelIncome)}</div>
            <div className="tt-sub">since last cutoff</div>
          </div>
        </div>
      </GlassCard>

      <div className="mt-4" />

      {/* ─────────── INCOME BREAKDOWN + CEILING ─────────── */}
      <div className="grid grid-2">
        <GlassCard title="Income Breakdown">
          <div className="income-breakdown-grid">
            <DonutChart
              slices={incomeSlices}
              size={200}
              thickness={22}
              centerLabel="Total"
              centerValue={fmtUSD(income.total)}
            />
            <div className="donut-legend">
              {incomeSlices.map((s, i) => (
                <div key={i} className="donut-legend-item" style={{ borderLeftColor: s.color }}>
                  <span className="swatch" style={{ background: s.color }} />
                  <span className="name">{s.label}</span>
                  <span className="val">{fmtUSD(s.value)}</span>
                  <span className="pct">
                    {income.total > 0n ? ((Number(s.value) * 100) / Number(income.total)).toFixed(1) : '0.0'}%
                  </span>
                </div>
              ))}
            </div>
          </div>
          {income.total === 0n && !income.loading && !income.error && (
            <div className="alert a-info mt-3">
              No income recorded yet — sync Farming Profit or wait for the next cutoff.
            </div>
          )}
          {income.error && (
            <div className="alert a-warn mt-3">
              Could not load income breakdown: {income.error}
            </div>
          )}
        </GlassCard>

        <GlassCard title={`Ceiling Progress · ${ceilLabel}`}>
          <Speedometer
            pct={ceiling.pct}
            label="Bucket Fill"
            valueText={fmtUSD(ceiling.earned) + ' / ' + fmtUSD(ceiling.cap)}
          />
          <div className="mt-3">
            <ProgressBar value={Number(ceiling.earned)} max={Number(ceiling.cap) || 1} />
            <div className="progress-labels">
              <span>Earned across active investments</span>
              <span>{fmtUSD(ceiling.cap > 0n ? ceiling.cap - ceiling.earned : 0n)} remaining</span>
            </div>
          </div>
        </GlassCard>
      </div>

      <div className="mt-4" />

      {/* ─────────── RECENT ACTIVITY (5 + expand + paginate) ─────────── */}
      <GlassCard
        title="Recent Activity"
        actions={
          <div className="activity-header-actions">
            <select
              className="activity-filter-select"
              value={actFilter}
              onChange={(e) => { setActFilter(e.target.value); setPage(0); }}
              aria-label="Filter activity"
            >
              {ACT_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
            {!expanded ? (
              <NeonButton size="sm" variant="ghost" onClick={() => { setExpanded(true); setPage(0); }}>
                View More
              </NeonButton>
            ) : (
              <NeonButton size="sm" variant="ghost" onClick={() => setExpanded(false)}>
                Show Less
              </NeonButton>
            )}
          </div>
        }
      >
        {activity.loading ? (
          <div className="empty-state"><AtomSpinner size="lg" /><div className="empty-title mt-3">Fetching Events</div></div>
        ) : filteredEvents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">◯</div>
            <div className="empty-title">{actFilter === 'all' ? 'No recent activity' : 'No events for this filter'}</div>
            <div className="mt-2">{actFilter === 'all' ? 'Your on-chain actions will appear here.' : 'Try a different category or "All".'}</div>
          </div>
        ) : (
          <>
            <div className="activity-list">
              {shownEvents.map((ev, i) => {
                const d = describeActivity(ev);
                return (
                  <div key={page * PAGE + i} className={`activity-row a-${d.color}`}>
                    <div className="activity-icon">{d.icon}</div>
                    <div className="activity-body">
                      <div className="activity-title">{d.title}</div>
                      {d.detail && <div className="activity-detail">{d.detail}</div>}
                    </div>
                    <div className="activity-time">{timeAgo(ev.ts)}</div>
                  </div>
                );
              })}
            </div>

            {expanded && activity.events.length > PAGE && (
              <div className="pagination">
                <span>Page {page + 1} of {totalPages} · {activity.events.length} events</span>
                <div className="pagination-btns">
                  <NeonButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >Prev</NeonButton>
                  <NeonButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >Next</NeonButton>
                </div>
              </div>
            )}
          </>
        )}
      </GlassCard>

      <VideoTiles />

      {busy && <HexLoader label={txLabel} sub="Please confirm the transaction in your wallet." />}
    </div>
  );
};

export default Dashboard;
